import { useBlockEnvironment } from '@zillow/cms-blocks';
import { Divider, Heading, List, Paragraph } from '@zillow/constellation-10';
import { Group, ZillowResearchLayout } from '@zillow/zrs-cms';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';

import { REVALIDATION_INTERVAL } from '../../../server/config';
import GlobalStyle from '../../components/GlobalStyle';
import { ResearchDataSectionComponent } from '../../components/ResearchData/ResearchData';
import { RESEARCH_DATA_SECTIONS } from '../../components/ResearchData/ResearchData.data';

export interface ResearchDataPageProps {
    useVisualRefreshTheme?: boolean;
}

/**
 * Research Data page component that displays Zillow research housing data downloads.
 *
 * @component
 */
export function ResearchDataPage({
    useVisualRefreshTheme: _useVisualRefreshTheme,
}: ResearchDataPageProps): JSX.Element {
    const router = useRouter();
    const { environment = 'prod' } = useBlockEnvironment();

    return (
        <ZillowResearchLayout router={router} environment={environment}>
            <Head>
                <title>Housing Data | Zillow Research</title>
                <meta
                    name="description"
                    content="Download free housing data on home values, rentals, sales, inventory, and more from Zillow Research."
                />
            </Head>
            <GlobalStyle />
            <Group
                maxWidth={{ default: 'full' }}
                backgroundColor="gray100"
                padding={{
                    paddingTop: { default: 'xl' },
                    paddingBottom: { default: 'xl' },
                    // Horizontal padding is deliberately 0: it never rendered
                    // before (the props were passed top-level, which Group
                    // ignores), so keeping it at 0 limits this change to the
                    // vertical spacing CMS-5544 asked for. Adding gutters here
                    // would also affect narrow viewports.
                    paddingRight: { default: '0' },
                    paddingLeft: { default: '0' },
                }}
            >
                <Group maxWidth={{ default: 'md' }} alignItems="stretch">
                    {/* v8 fontType={{ default: 'h2', md_lte: 'h3' }} is desktop-first:
                        h3 up to the top of the md range (max-width 768px) and h2 above
                        it. Panda is mobile-first, so that becomes
                        { base: 'heading-md', lg: 'heading-lg' } — Panda `lg` is 48em =
                        768px against v8's lg starting at 769px.

                        h3 = 36px/40px = heading-md exactly. h2 (48px/56px) has no exact
                        v10 equivalent: heading-lg is 44px/48px and heading-xl is
                        60px/72px, so the desktop h1 renders 4px smaller than v8. Kept on
                        heading-lg to stay on the v10 type scale rather than pinning the
                        old pixels — same call as AuthorHeader (MOPS-25873). The
                        responsive.heading-lg token is not usable here: it is a three-step
                        32/40/44 ramp, which does not reproduce v8's two-step 36→48.

                        textStyle goes through css rather than the prop because the prop
                        takes a single token. fontFamily is dropped — heading-md/-lg
                        already set font-family: var(--fonts-heading), which is
                        var(--fonts-brand), the v10 mapping of v8 'serif'.

                        v8 marginBottom="sm" = 16px → 'default'. */}
                    <Heading
                        level={1}
                        css={{
                            textStyle: { base: 'heading-md', lg: 'heading-lg' },
                            marginBottom: 'default',
                            textAlign: 'center',
                        }}
                    >
                        Housing Data
                    </Heading>
                    {/* v8 `fontSize="large"` and `color="textMedium"` are dropped, not
                        translated: neither is a v8 Text/Paragraph prop (v8 exposes
                        fontType and fontColor), so styled-components discarded them and
                        this paragraph has always rendered at the default body ramp in
                        the default text color. v10 Paragraph *does* accept `color`, so
                        carrying it over would newly turn the copy grey — a change, not
                        parity. Same reasoning as the Group horizontal padding below. */}
                    <Paragraph css={{ textAlign: 'center' }}>
                        Download Zillow&apos;s home value index, rent index,
                        forecasts, for-sale inventory, and other housing data
                        for use in your own analyses.
                    </Paragraph>
                </Group>
            </Group>

            {/* Main content area with all data sections */}
            <Group
                maxWidth={{ default: 'lg' }}
                padding={{
                    paddingTop: { default: 'md' },
                    // paddingBottom 0 so the trailing divider keeps the same
                    // xl/xl spacing as the dividers between sections.
                    paddingBottom: { default: '0' },
                    // See the hero group: horizontal padding stays 0.
                    paddingRight: { default: '0' },
                    paddingLeft: { default: '0' },
                }}
                alignItems="stretch"
            >
                {RESEARCH_DATA_SECTIONS.map((section) => (
                    <React.Fragment key={section.title}>
                        <ResearchDataSectionComponent section={section} />
                        {/* v8 marginTop/marginBottom="xl" = 48px → marginBlock
                            'layout.looser' (spacing-1200 = 3rem). v10 Divider has no
                            spacing props, and its default tone="muted" reproduces v8's
                            default appearance: Divider.muted.bg resolves to
                            border.muted → gray.300 → #CDCDD3, the same hex as
                            ThemeSkunkWorks Divider.regular.backgroundColor, at the same
                            1px weight and 100% width. */}
                        <Divider css={{ marginBlock: 'layout.looser' }} />
                    </React.Fragment>
                ))}
            </Group>

            {/* Definitions section */}
            <Group
                maxWidth={{ default: 'lg' }}
                padding={{
                    // paddingTop 0 pairs with the sections group above: the
                    // divider's own xl margins provide the gap.
                    paddingTop: { default: '0' },
                    paddingBottom: { default: 'lg' },
                    // See the hero group: horizontal padding stays 0.
                    paddingRight: { default: '0' },
                    paddingLeft: { default: '0' },
                }}
                alignItems="stretch"
            >
                {/* Same h4 → heading-sm mapping as the section headings in
                    ResearchData.tsx; marginBottom="md" = 24px → 'loose'. */}
                <Heading
                    level={2}
                    textStyle="heading-sm"
                    css={{ marginBottom: 'loose' }}
                >
                    Definitions of Home Types
                </Heading>
                {/* v8 <List> → v10 <List.Root>. The defaults line up: v10
                    List.Root's recipe sets textStyle 'body' (v8 List.fontType =
                    'body'), paddingInlineStart List.padding.nested-horz → spacing.looser
                    = 32px (v8 List.paddingLeft = 32) and appearance 'bullet'.

                    List.Root's recipe also sets `color: List.label`, which would break
                    colour inheritance from a parent Group's fontColor. Not an issue here
                    — this Group sets no fontColor, and List.label resolves to
                    text.neutral, the colour the list already inherited. */}
                <List.Root>
                    <List.Item>
                        <Paragraph>
                            <strong>All Homes:</strong> Zillow defines all homes
                            as single-family, condominium and co-operative homes
                            with a county record. Unless specified, all series
                            cover this segment of the housing stock.
                        </Paragraph>
                    </List.Item>
                    <List.Item>
                        <Paragraph>
                            <strong>All Homes Plus Multifamily:</strong> In
                            addition to single-family, condominium and
                            co-operative homes, this group includes units in
                            buildings with 5 or more residential units.
                        </Paragraph>
                    </List.Item>
                    <List.Item>
                        <Paragraph>
                            <strong>Single-Family Residence (SFR):</strong>{' '}
                            Single-family residence, a free-standing building
                            that does not share walls with another building.
                        </Paragraph>
                    </List.Item>
                    <List.Item>
                        <Paragraph>
                            <strong>Condo/Co-op:</strong> Condominiums and
                            co-operative homes, which are units in buildings
                            with shared ownership structures. These typically
                            include apartments and townhouses.
                        </Paragraph>
                    </List.Item>
                    <List.Item>
                        <Paragraph>
                            <strong>Multi-Family Residence (MFR):</strong> A
                            residential building with 5 or more individual
                            rental units.
                        </Paragraph>
                    </List.Item>
                </List.Root>
            </Group>
        </ZillowResearchLayout>
    );
}

/**
 * Generates static props for the Research Data page.
 * Uses Incremental Static Regeneration (ISR) to update the page periodically.
 *
 * @returns Static props for the page with Visual Refresh theme enabled
 */
export async function getStaticProps() {
    return {
        props: {
            useVisualRefreshTheme: true,
        },
        revalidate: REVALIDATION_INTERVAL,
    };
}

export default ResearchDataPage;
